import React from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

/**
 * Gold ring spinner.
 *
 * Avoids `filter: drop-shadow` on the rotating ring — Brave (and some Chromium
 * builds) composite that poorly with transparent borders + transform, which
 * collapses the spinner into a thin crescent. Glow lives on static layers only.
 */
export default function Loader({ size = 22 }) {
    const isHero = size >= 56;
    // Compact: keep the Inviting spinner weight. Hero: slightly heavier stroke.
    const ringWidth = isHero
        ? Math.max(3.5, Math.round(size * 0.055 * 10) / 10)
        : size >= 28
          ? 2
          : 1.5;
    const innerRingWidth = isHero
        ? Math.max(1.5, Math.round(size * 0.028 * 10) / 10)
        : 1;
    const coreSizePct = isHero ? "18%" : "22%";
    const glowBlur = isHero ? Math.round(size * 0.22) : 4;
    const coreGlow = isHero ? Math.round(size * 0.28) : 8;

    return (
        <Box
            sx={{
                position: "relative",
                width: size,
                height: size,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                filter: isHero
                    ? "none"
                    : `drop-shadow(0 0 ${glowBlur}px rgba(232,163,61,0.2))`,
            }}
        >
            {isHero && (
                <>
                    {/* Ambient bloom — soft atmospheric glow behind everything */}
                    <MotionBox
                        aria-hidden
                        animate={{ opacity: [0.45, 0.75, 0.45], scale: [0.92, 1.04, 0.92] }}
                        transition={{
                            duration: 2.8,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        sx={{
                            position: "absolute",
                            inset: "-18%",
                            borderRadius: "50%",
                            background: `
                                radial-gradient(
                                    circle,
                                    rgba(232,163,61,0.28) 0%,
                                    rgba(232,163,61,0.12) 38%,
                                    rgba(232,163,61,0.03) 62%,
                                    transparent 72%
                                )
                            `,
                            pointerEvents: "none",
                            "@media (prefers-reduced-motion: reduce)": {
                                animation: "none",
                                opacity: 0.55,
                                transform: "none",
                            },
                        }}
                    />

                    {/* Soft halo ring — static depth under the rotating sweep */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            boxShadow: `
                                0 0 ${Math.round(size * 0.08)}px rgba(232,163,61,0.2),
                                0 0 ${Math.round(size * 0.2)}px rgba(232,163,61,0.18),
                                0 0 ${Math.round(size * 0.38)}px rgba(232,163,61,0.1),
                                inset 0 0 ${Math.round(size * 0.12)}px rgba(232,163,61,0.08)
                            `,
                            pointerEvents: "none",
                        }}
                    />

                    {/* Faint track ring so the stroke reads as a full orbit */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            border: `${ringWidth}px solid rgba(232,163,61,0.14)`,
                            boxSizing: "border-box",
                            boxShadow: `
                                0 0 ${Math.round(size * 0.1)}px rgba(232,163,61,0.22),
                                inset 0 0 ${Math.round(size * 0.06)}px rgba(232,163,61,0.1)
                            `,
                            pointerEvents: "none",
                        }}
                    />
                </>
            )}

            {/* OUTER RING — conic sweep, no partial-border trick */}
            <MotionBox
                animate={{ rotate: 360 }}
                transition={{
                    duration: isHero ? 1.25 : 1.1,
                    repeat: Infinity,
                    ease: "linear",
                }}
                sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: isHero
                        ? `
                            conic-gradient(
                                from 0deg,
                                rgba(232,163,61,0.04) 0deg,
                                rgba(232,163,61,0.1) 90deg,
                                rgba(232,163,61,0.35) 180deg,
                                rgba(232,163,61,0.72) 270deg,
                                rgba(255,214,140,0.98) 330deg,
                                rgba(255,240,200,1) 348deg,
                                rgba(232,163,61,0.04) 360deg
                            )
                        `
                        : `
                            conic-gradient(
                                from 0deg,
                                rgba(232,163,61,0.06) 0deg,
                                rgba(232,163,61,0.16) 110deg,
                                rgba(232,163,61,0.45) 220deg,
                                rgba(232,163,61,0.92) 330deg,
                                rgba(232,163,61,0.06) 360deg
                            )
                        `,
                    WebkitMaskImage: `
                        radial-gradient(
                            farthest-side,
                            transparent calc(100% - ${ringWidth}px),
                            #000 calc(100% - ${ringWidth}px)
                        )
                    `,
                    maskImage: `
                        radial-gradient(
                            farthest-side,
                            transparent calc(100% - ${ringWidth}px),
                            #000 calc(100% - ${ringWidth}px)
                        )
                    `,
                }}
            />

            {/* INNER RING */}
            <MotionBox
                animate={{ rotate: -360 }}
                transition={{
                    duration: isHero ? 2.8 : 2.4,
                    repeat: Infinity,
                    ease: "linear",
                }}
                sx={{
                    position: "absolute",
                    width: "58%",
                    height: "58%",
                    borderRadius: "50%",
                    border: `${innerRingWidth}px solid ${
                        isHero
                            ? "rgba(232,163,61,0.16)"
                            : "rgba(255,255,255,0.1)"
                    }`,
                    borderBottomColor: isHero
                        ? "rgba(255,220,160,0.72)"
                        : "rgba(255,255,255,0.4)",
                    ...(isHero
                        ? { borderLeftColor: "rgba(232,163,61,0.28)" }
                        : {}),
                    boxSizing: "border-box",
                    boxShadow: isHero
                        ? `0 0 ${Math.round(size * 0.08)}px rgba(232,163,61,0.2)`
                        : "none",
                }}
            />

            {/* CENTER CORE */}
            <MotionBox
                animate={
                    isHero
                        ? { opacity: [0.88, 1, 0.88], scale: [0.96, 1.06, 0.96] }
                        : undefined
                }
                transition={
                    isHero
                        ? {
                              duration: 1.8,
                              repeat: Infinity,
                              ease: "easeInOut",
                          }
                        : undefined
                }
                sx={{
                    width: coreSizePct,
                    height: coreSizePct,
                    minWidth: 3,
                    minHeight: 3,
                    borderRadius: "50%",
                    background: `
                        radial-gradient(
                            circle,
                            rgba(255,255,255,0.98) 0%,
                            rgba(255,220,150,0.95) 28%,
                            rgba(232,163,61,0.9) 55%,
                            rgba(232,163,61,0.15) 100%
                        )
                    `,
                    boxShadow: isHero
                        ? `
                            0 0 ${Math.round(coreGlow * 0.35)}px rgba(255,240,200,0.95),
                            0 0 ${Math.round(coreGlow * 0.65)}px rgba(232,163,61,0.7),
                            0 0 ${coreGlow}px rgba(232,163,61,0.45),
                            0 0 ${Math.round(coreGlow * 1.6)}px rgba(232,163,61,0.22)
                        `
                        : `0 0 ${coreGlow}px rgba(232,163,61,0.45)`,
                    "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                        opacity: 1,
                        transform: "none",
                    },
                }}
            />
        </Box>
    );
}
